import { type Breakpoint } from '@divi/types';

import {
  type MultiViewDataItemVisibility,
  type MultiViewDataUpdateVisibility,
  type MultiViewUpdateElementParams,
} from '../../types';
import { addEventListenerHover } from '../add-event-listener-hover';
import { dispatchCustomEvent } from '../dispatch-custom-event';
import { parseData } from '../parse-data';

/**
 * Sets the visibility of an element and dispatches a custom event.
 *
 * @since ??
 *
 * @param {object} params The parameters needed to set the visibility of an element.
 * @param {MultiViewDataItemVisibility} params.stateData The data to set as the visibility.
 * @param {MultiViewDataUpdateVisibility} params.item The data item being updated.
 * @param {HTMLElement} params.element The element to update.
 * @param {Breakpoint.Name} params.breakpoint The current breakpoint.
 * @param {string} params.state The state of the element ('default' or 'hover').
 * @param {MouseEvent | TouchEvent} params.originalEvent The original event that triggered the update.
 */
const setElementVisibility = ({
  stateData,
  item,
  element,
  breakpoint,
  state,
  originalEvent,
}: MultiViewUpdateElementParams<MultiViewDataItemVisibility>) => {
  let updatedData: MultiViewDataItemVisibility;
  let updated = false;

  if ('hidden' === stateData && !element.classList.contains('et_multi_view_hidden')) {
    element.classList.add('et_multi_view_hidden');
    updated = true;
    updatedData = stateData;
  }

  if ('visible' === stateData && element.classList.contains('et_multi_view_hidden')) {
    element.classList.remove('et_multi_view_hidden');
    updated = true;
    updatedData = stateData;
  }

  if (updated) {
    // Dispatch custom event: divi:multiView:setVisibility
    dispatchCustomEvent<MultiViewDataItemVisibility>({
      breakpoint,
      item,
      originalEvent,
      state,
      updatedData,
    });
  }
};

/**
 * Update element visibility.
 *
 * This function is used to update the element visibility by adding and removing `et_multi_view_hidden` class name.
 *
 * @since ??
 *
 * @param {object} params The parameters needed to update the visibility of an element.
 * @param {MultiViewDataUpdateVisibility} params.item The item that will be updated.
 * @param {Breakpoint.Name} params.breakpoint The current breakpoint, which can be 'phone', 'tablet', or 'desktop'.
 * @param {boolean} params.isInit Whether this is the first time the function is being called.
 * @param {Breakpoint.Name} params.baseBreakpoint The base breakpoint.
 * @param {Breakpoint.Names} params.breakpointNames The list of breakpoint names.
 */
export const updateDataVisibility = ({
  item,
  breakpoint,
  isInit = false,
  baseBreakpoint = 'desktop',
  breakpointNames = ['desktop', 'tablet', 'phone'],
}: {
  item: MultiViewDataUpdateVisibility;
  breakpoint: Breakpoint.Name;
  isInit: boolean;
  baseBreakpoint: Breakpoint.Name;
  breakpointNames: Breakpoint.Names;
}): void => {
  const { element, hoverElements, data } = item;
  const { defaultStateData, hoverStateData } = parseData<MultiViewDataItemVisibility>({
    data,
    breakpoint,
    baseBreakpoint,
    breakpointNames,
  });

  // Bail early default state data is null.
  // This can happen if the data is not valid.
  if (null === defaultStateData) {
    return;
  }

  const switchDataToDefaultState = (event?: MouseEvent | TouchEvent) => {
    setElementVisibility({
      stateData: defaultStateData,
      state: 'default',
      element,
      item,
      breakpoint,
      originalEvent: event,
    });
  };

  // Handle hover state.
  if (null !== hoverStateData) {
    const switchDataToHoverState = (event?: MouseEvent | TouchEvent) => {
      setElementVisibility({
        stateData: hoverStateData,
        state: 'hover',
        element,
        item,
        breakpoint,
        originalEvent: event,
      });
    };

    hoverElements.forEach(hoverElement => {
      addEventListenerHover(
        element,
        hoverElement,
        switchDataToDefaultState,
        switchDataToHoverState,
        'update-data-visibility',
      );
    });
  }

  // Switch to default state on page load or when the breakpoint changes that triggered when the user
  // resizing the browser or changing the device orientation.
  if ('desktop' === breakpoint && isInit && !item.switchOnLoad) {
    return;
  }

  switchDataToDefaultState();
};
